/**
 * useGeometryContextMenu Hook
 *
 * Provides context menu handling for geometries (Edit/Delete geometry actions).
 * Works with MapLibre GL JS events for Line and Shape geometries.
 *
 * Features:
 * - Desktop: Handles contextmenu event (right-click)
 * - Mobile: Handles long-press (500ms) via touch events
 * - Shows GeometryContextMenu component via unified Zustand state
 * - Condition: Only when !isDrawing && isGeometryEditingActive && !isPreview
 * - Works with interactive hitbox layers for Line/Shape
 *
 * Usage:
 * - Line/Shape: Pass interactiveLayerId (hitbox layer)
 * - Marker: Use HTML ref directly (not this hook)
 *
 * @module hooks/useGeometryContextMenu
 * @example
 * ```typescript
 * // In LineRenderer or ShapeRenderer:
 * useGeometryContextMenu({
 *   geometryId: geometry.id,
 *   geometryType: GeometryType.Line,
 *   interactiveLayerId: `line-hitbox-${geometry.id}`,
 *   isPreview: false,
 * });
 * ```
 */

import { useCallback, useEffect } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import { useGeoMarkupStore } from '../store';
import { selectIsGeometryEditingActive } from '../store/selectors';
import type { GeometryType } from '../types/geometry';
import type { MapLayerMouseEvent } from 'maplibre-gl';
import { useMapLayerLongPress } from './gestures';

/**
 * Options for useGeometryContextMenu hook
 */
export interface UseGeometryContextMenuOptions {
  /** ID of the geometry */
  geometryId: string;
  /** Type of geometry (Marker, Line, Shape) */
  geometryType: GeometryType;
  /** ID of the interactive layer (hitbox layer) */
  interactiveLayerId: string;
  /** Whether this is a preview geometry */
  isPreview?: boolean;
}

/**
 * Hook for handling geometry right-click context menu
 *
 * Registers contextmenu event listener on geometry's interactive layer.
 * Shows universal GeometryContextMenu component when conditions met.
 *
 * @param options - Hook configuration options
 */
export const useGeometryContextMenu = ({
  geometryId,
  geometryType,
  interactiveLayerId,
  isPreview = false,
}: UseGeometryContextMenuOptions) => {
  const { current: map } = useMap();

  // Zustand state (atomic selectors)
  const isDrawing = useGeoMarkupStore((s) => s.isDrawing);
  const isGeometryEditingActive = useGeoMarkupStore(selectIsGeometryEditingActive);

  // Unified context menu action (from uiSlice)
  const showContextMenu = useGeoMarkupStore((s) => s.ui.showContextMenu);

  /**
   * Show geometry context menu via unified Zustand state
   */
  const showGeometryContextMenu = useCallback(
    (position: { x: number; y: number }) => {
      showContextMenu('geometry', position, {
        geometryId,
        geometryType,
      });
    },
    [showContextMenu, geometryId, geometryType]
  );

  // ========== LONG-PRESS (MOBILE) ==========
  // Uses unified useMapLayerLongPress hook
  // NOTE: Movement cancellation not available (MapLibre limitation)
  useMapLayerLongPress(interactiveLayerId, {
    enabled: !isDrawing && isGeometryEditingActive && !isPreview,
    onLongPress: showGeometryContextMenu,
  });

  /**
   * Handle right-click on geometry (desktop)
   *
   * Shows context menu with Edit and Delete options.
   * Only works when NOT drawing and form is open.
   */
  const handleContextMenu = useCallback(
    (e: MapLayerMouseEvent) => {
      // Only show menu when NOT drawing, form is open, and NOT preview
      if (!isDrawing && isGeometryEditingActive && !isPreview) {
        e.preventDefault();
        e.originalEvent.stopPropagation();

        // Show universal geometry context menu
        showGeometryContextMenu({
          x: e.originalEvent.clientX,
          y: e.originalEvent.clientY,
        });
      }
    },
    [isDrawing, isGeometryEditingActive, isPreview, showGeometryContextMenu]
  );

  /**
   * Setup desktop contextmenu event listener
   */
  useEffect(() => {
    if (!map || isPreview) return;

    // Register contextmenu event on interactive hitbox layer (desktop)
    map.on('contextmenu', interactiveLayerId, handleContextMenu);

    return () => {
      map.off('contextmenu', interactiveLayerId, handleContextMenu);
    };
  }, [map, interactiveLayerId, handleContextMenu, isPreview]);

  return {
    /** Handler for manual usage if needed */
    handleContextMenu,
  };
};
